// Public Amenity Booking flow — mounted at /book-amenity/:amenityId and
// /book-amenity/confirmation/:bookingRef. Multi-step wizard:
//   1. Pick an event date
//   2. Show that month's availability chart with the selected date highlighted
//   3. Booking form (applicant, event, facilities, billing)
//   4. Summary + T&C acceptance
//   5. Payment (CCAvenue online OR NEFT bank transfer)
//   6. Confirmation with receipt download
//
// Uses the same visual language as the Trust site (Royal Blue #0D5C63 +
// Gold #F26419 + Ivory cream) and reuses public/amenity-bookings endpoints.
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Loader2, ChevronLeft, ChevronRight, CheckCircle2,
  AlertTriangle, XCircle, Clock,
  Download, ArrowRight, ArrowLeft, Sparkles, Info,
} from "lucide-react";

const inputCls = "w-full bg-white border border-[#E9E2D2] text-[#1c1a17] rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#0D5C63] focus:border-[#0D5C63]";
const labelCls = "text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5";

function fmtINR(n) {
  const v = Number(n) || 0;
  return `₹ ${v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function todayYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/* -------------------------- Availability calendar ------------------------- */
function AvailabilityCalendar({ amenityId, year, month, selectedDate, onSelect, onChangeMonth }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/public/amenities/${amenityId}/availability`, { params: { year, month } });
        if (!cancel) setData(data);
      } catch (err) {
        if (!cancel) toast.error(err?.response?.data?.detail || "Could not load availability");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [amenityId, year, month]);

  const firstDow = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  const today = todayYMD();
  const monthName = new Date(year, month - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });

  const statusOf = (day) => {
    const key = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return (data?.dates || {})[key] || "Available";
  };
  const halvesOf = (day) => {
    const key = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return (data?.halves_booked || {})[key] || [];
  };
  const isPast = (day) => {
    const key = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return key < today;
  };

  return (
    <div className="bg-white border border-[#E9E2D2] rounded-2xl p-5 md:p-6" data-testid="ba-availability">
      <div className="flex items-center justify-between mb-5">
        <button type="button" onClick={() => onChangeMonth(-1)} data-testid="ba-cal-prev" className="p-2 hover:bg-[#F5F0E4] rounded-full text-[#0D5C63]" aria-label="Previous month">
          <ChevronLeft size={18} />
        </button>
        <div className="trust-serif text-xl md:text-2xl text-[#1A1A1A]" data-testid="ba-cal-month">{monthName}</div>
        <button type="button" onClick={() => onChangeMonth(1)} data-testid="ba-cal-next" className="p-2 hover:bg-[#F5F0E4] rounded-full text-[#0D5C63]" aria-label="Next month">
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="grid grid-cols-7 text-center text-[10px] tracking-widest uppercase text-[#8a8478] font-bold mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
      </div>
      {loading ? (
        <div className="text-center py-10 text-sm text-[#5B5B5B]"><Loader2 size={16} className="inline animate-spin mr-2" />Loading&hellip;</div>
      ) : (
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((day, i) => {
            if (day === null) return <div key={`e${i}`} />;
            const key = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const s = statusOf(day);
            const halves = halvesOf(day);
            const past = isPast(day);
            const sel = selectedDate === key;
            const cls =
              past ? "bg-[#F5F0E4] text-[#c7c1b5] cursor-not-allowed" :
              s === "Booked" ? "bg-[#FDECEC] text-[#B02020] border border-[#B02020]/30 cursor-not-allowed" :
              s === "Partially Booked" ? "bg-[#FFF3DF] text-[#8a5a1a] border border-[#c78e42]/60 hover:bg-[#FDE7B8]" :
              s === "Provisionally Booked" ? "bg-[#FEF7E4] text-[#8a5a1a] border border-[#c78e42]/40 hover:bg-[#FBEDC7]" :
              "bg-[#E7F3F1] text-[#0D5C63] border border-[#0D5C63]/25 hover:bg-[#CFE7E2]";
            const disabled = past || s === "Booked";
            const halvesTag = halves.length ? ` · ${halves.join(" & ")} booked` : "";
            return (
              <button
                key={key}
                type="button"
                onClick={() => !disabled && onSelect(key)}
                disabled={disabled}
                data-testid={`ba-cal-day-${key}`}
                data-status={s}
                aria-label={`${key} — ${s}${halvesTag}`}
                title={halves.length ? `${halves.join(" & ")} half already booked` : s}
                className={`relative aspect-square rounded-lg text-sm font-semibold transition-colors ${cls} ${sel ? "ring-2 ring-[#F26419] ring-offset-1" : ""}`}
              >
                {day}
                {sel && <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-[#F26419]" />}
                {s === "Partially Booked" && halves.length === 1 && (
                  <span className="absolute bottom-1 left-1 right-1 text-[8px] leading-none font-bold tracking-widest text-[#8a5a1a]" data-testid={`ba-cal-half-${key}`}>
                    {halves[0]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      <div className="mt-5 flex items-center justify-center gap-4 flex-wrap text-[11px] text-[#5B5B5B]">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[#E7F3F1] border border-[#0D5C63]/25" /> Available</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[#FFF3DF] border border-[#c78e42]/60" /> Partially Booked (one half free)</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[#FEF7E4] border border-[#c78e42]/40" /> Provisionally Booked</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-[#FDECEC] border border-[#B02020]/30" /> Booked</span>
      </div>
    </div>
  );
}

/* ------------------------------- Main page -------------------------------- */
const STAY_TYPES = new Set(["Hostel", "Guest House"]);
const STAY_MAX_NIGHTS = 5;

function BalanceDue({ booking, failed, paymentStatus }) {
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try { await startAmenityPayment(booking.booking_ref); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); setBusy(false); }
  };
  return (
    <>
      <div className={`mx-auto h-16 w-16 rounded-full inline-flex items-center justify-center ${failed ? "bg-[#FDECEC] text-[#B02020]" : "bg-[#FEF7E4] text-[#8a5a1a]"}`}>
        {failed ? <XCircle size={32} /> : <Clock size={32} />}
      </div>
      <h1 className="trust-serif text-3xl md:text-4xl text-[#1A1A1A] mt-4" data-testid="ba-conf-title">
        {failed ? `Payment ${paymentStatus === "cancelled" ? "Cancelled" : "Failed"}` : "Balance Payment Pending"}
      </h1>
      <p className="text-sm text-[#5B5B5B] mt-2 max-w-lg mx-auto" data-testid="ba-conf-balance-text">
        Voucher <b className="font-mono">{booking.voucher_code}</b> ({booking.voucher_label || "discount"}) saved you <b>{fmtINR(booking.voucher_discount)}</b>.
        Pay the remaining <b>{fmtINR(booking.total_amount)}</b> to confirm your booking. Your voucher stays valid until the payment succeeds.
      </p>
      <button type="button" onClick={pay} disabled={busy} data-testid="ba-conf-pay-balance"
        className="mt-5 inline-flex items-center gap-2 bg-[#F26419] hover:bg-[#0D5C63] text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Pay {fmtINR(booking.total_amount)} now
      </button>
    </>
  );
}

async function startAmenityPayment(bookingRef) {
  const { data } = await api.post(`/public/amenity-bookings/${bookingRef}/payment/initiate`);
  const form = document.createElement("form");
  form.method = "POST";
  form.action = data.gateway_url;
  [["encRequest", data.enc_request], ["access_code", data.access_code]].forEach(([name, value]) => {
    const i = document.createElement("input");
    i.type = "hidden"; i.name = name; i.value = value;
    form.appendChild(i);
  });
  document.body.appendChild(form);
  form.submit();
}

export default function BookAmenity() {
  const { amenityId } = useParams();
  const navigate = useNavigate();
  const [amenity, setAmenity] = useState(null);
  const [err, setErr] = useState("");
  const [step, setStep] = useState(1); // 1 date, 2 form, 3 summary, 4 payment
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState("");
  const [checkInDate, setCheckInDate] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");
  const [form, setForm] = useState({
    duration: "Half Day",
    time_slot: "",
    start_time: "",
    end_time: "",
    applicant_name: "",
    mobile: "",
    email: "",
    organization: "",
    event_name: "",
    event_type: "",
    event_purpose: "",
    attendees: "",
    seating: "",
    av_equipment: [],
    catering: [],
    additional_facilities: [],
    billing_name: "",
    billing_address: "",
    gst_number: "",
    remarks: "",
    voucher_code: "",
  });
  const [voucher, setVoucher] = useState(null); // {code, expires_at, ...} once verified
  const [voucherError, setVoucherError] = useState("");
  const [voucherChecking, setVoucherChecking] = useState(false);
  const [accept, setAccept] = useState(false);
  const [booking, setBooking] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/amenities/${amenityId}`);
        setAmenity(data);
        if (!data?.booking_enabled) setErr("Online booking is not enabled for this amenity.");
      } catch (e) {
        setErr(e?.response?.data?.detail || "Amenity not found");
      }
    })();
  }, [amenityId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (k, name, on) => setForm((f) => {
    const cur = new Set(f[k] || []);
    if (on) cur.add(name); else cur.delete(name);
    return { ...f, [k]: Array.from(cur) };
  });

  // Auto-calculate end_time = start_time + 4 or 8 hours whenever start_time
  // or duration changes. Duration hours: Half Day = 4h, Full Day = 8h.
  const DURATION_HOURS = { "Half Day": 4, "Full Day": 8 };
  useEffect(() => {
    const st = (form.start_time || "").trim();
    if (!/^\d{2}:\d{2}$/.test(st)) return;
    const [h, m] = st.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return;
    const add = DURATION_HOURS[form.duration] || 0;
    const total = h * 60 + m + add * 60;
    const eh = Math.floor((total / 60) % 24);
    const em = total % 60;
    const et = `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
    if (et !== form.end_time) setForm((f) => ({ ...f, end_time: et }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.start_time, form.duration]);

  const isStay = !!amenity && STAY_TYPES.has(amenity.amenity_type);
  const nights = useMemo(() => {
    if (!isStay || !checkInDate || !checkOutDate) return 0;
    const a = new Date(checkInDate);
    const b = new Date(checkOutDate);
    const diff = Math.round((b - a) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  }, [isStay, checkInDate, checkOutDate]);

  // ------- Live pricing preview (mirrors backend) -------
  const pricing = useMemo(() => {
    if (!amenity) return null;
    let base;
    if (isStay) {
      base = Number(amenity.full_day_charge || 0) * nights;
    } else {
      base = form.duration === "Half Day" ? Number(amenity.half_day_charge || 0) : Number(amenity.full_day_charge || 0);
    }
    const additional = 0;
    const taxPct = Number(amenity.tax_percent || 0);
    const tax = Math.round((base + additional) * taxPct) / 100;
    const deposit = Number(amenity.security_deposit || 0);
    const gross = Math.round((base + additional + tax + deposit) * 100) / 100;
    const dv = Number(voucher?.discount_value || 0);
    const waived = !voucher ? 0
      : voucher.discount_type === "PERCENT" && dv < 100 ? Math.round(Math.min(gross, gross * dv / 100) * 100) / 100
      : voucher.discount_type === "FIXED" ? Math.min(gross, dv)
      : gross;
    const total = Math.round(Math.max(0, gross - waived) * 100) / 100;
    return { base, additional, catering: [], addfac: [], tax, taxPct, deposit, gross, waived, total, hasVoucher: !!voucher, isWaived: !!voucher && total <= 0, nights };
  }, [amenity, form.duration, voucher, isStay, nights]);

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    else if (m > 12) { m = 1; y += 1; }
    // Prevent going before current month
    const cy = now.getFullYear(); const cm = now.getMonth() + 1;
    if (y < cy || (y === cy && m < cm)) return;
    setYear(y); setMonth(m);
  };

  const proceedToForm = () => {
    if (isStay) {
      if (!checkInDate) return toast.error("Please select a check-in date");
      if (!checkOutDate) return toast.error("Please select a check-out date");
      if (new Date(checkOutDate) <= new Date(checkInDate)) return toast.error("Check-out must be after check-in");
      if (nights > STAY_MAX_NIGHTS) return toast.error(`Maximum stay is ${STAY_MAX_NIGHTS} nights`);
    } else {
      if (!selectedDate) return toast.error("Please select an event date");
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const validateForm = () => {
    if (!form.applicant_name.trim()) return "Applicant name is required";
    if (!/^\+?\d[\d\s-]{5,}$/.test(form.mobile.trim())) return "Please enter a valid mobile number";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return "Please enter a valid email address";
    if (!form.event_name.trim()) return isStay ? "Purpose of stay is required" : "Event name is required";
    if (!isStay) {
      if (!form.duration) return "Please choose Half Day or Full Day";
      if (!/^\d{2}:\d{2}$/.test(form.start_time)) return "Please choose an event start time";
    }
    if (!voucher) return "Please enter and apply your waiver voucher code";
    return "";
  };

  const proceedToSummary = () => {
    const msg = validateForm();
    if (msg) return toast.error(msg);
    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitBooking = async () => {
    if (!accept) return toast.error("Please accept the booking terms to proceed");
    setCreating(true);
    try {
      const payload = {
        amenity_id: amenityId,
        event_date: isStay ? checkInDate : selectedDate,
        check_in_date: isStay ? checkInDate : "",
        check_out_date: isStay ? checkOutDate : "",
        duration: isStay ? "Full Day" : form.duration,
        time_slot: isStay ? "" : form.time_slot,
        start_time: isStay ? "" : form.start_time,
        end_time: isStay ? "" : form.end_time,
        applicant_name: form.applicant_name.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        organization: form.organization.trim(),
        event_name: form.event_name.trim(),
        event_type: form.event_type.trim(),
        event_purpose: form.event_purpose.trim(),
        attendees: Number(form.attendees) || 0,
        seating: form.seating,
        av_equipment: form.av_equipment,
        catering: form.catering,
        additional_facilities: form.additional_facilities,
        billing_name: form.billing_name.trim() || form.applicant_name.trim(),
        billing_address: form.billing_address.trim(),
        gst_number: form.gst_number.trim(),
        remarks: form.remarks.trim(),
        voucher_code: voucher?.code || "",
        accept_terms: true,
      };
      const { data } = await api.post("/public/amenity-bookings", payload);
      setBooking(data);
      // Whether the booking auto-CONFIRMED via voucher or is now under review
      // (PROVISIONAL), the user goes straight to the confirmation screen.
      if (data.voucher_partial && data.status === "PROVISIONAL") {
        try {
          toast.success(`Voucher applied. Redirecting to pay the balance of ${fmtINR(data.total_amount)}…`);
          await startAmenityPayment(data.booking_ref);
          return;
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Could not start payment. You can retry from the booking page.");
        }
      }
      const paymentQuery = data.status === "CONFIRMED" ? "success" : "review";
      navigate(`/book-amenity/confirmation/${data.booking_ref}?payment=${paymentQuery}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create booking");
    } finally {
      setCreating(false);
    }
  };

  const applyVoucher = async () => {
    const code = (form.voucher_code || "").trim().toUpperCase();
    setVoucherError("");
    if (!code) return setVoucherError("Enter a voucher code");
    setVoucherChecking(true);
    try {
      const { data } = await api.post("/public/amenity-vouchers/validate", { code, amenity_id: amenityId });
      setVoucher(data);
      toast.success(`Voucher applied: ${data.discount_label || "100% waiver"}`);
    } catch (e) {
      setVoucher(null);
      const msg = e?.response?.data?.detail || "Voucher could not be validated";
      setVoucherError(msg);
      toast.error(msg);
    } finally { setVoucherChecking(false); }
  };

  const removeVoucher = () => {
    setVoucher(null);
    setForm((f) => ({ ...f, voucher_code: "" }));
    setVoucherError("");
  };

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20" data-testid="ba-error">
        <div className="bg-white border border-red-200 text-red-800 rounded-2xl p-6">
          <div className="inline-flex items-center gap-2 font-semibold"><AlertTriangle size={18} />{err}</div>
          <Link to="/amenities" className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#0D5C63] font-semibold underline"><ArrowLeft size={14} /> Back to Amenities</Link>
        </div>
      </div>
    );
  }
  if (!amenity) {
    return <div className="max-w-3xl mx-auto px-5 py-20 text-sm text-[#5B5B5B]" data-testid="ba-loading"><Loader2 size={16} className="inline animate-spin mr-2" />Loading&hellip;</div>;
  }

  return (
    <div className="bg-[#FAF6EB] min-h-screen" data-testid="ba-page">
      <section className="bg-gradient-to-br from-[#0D5C63] via-[#083E43] to-[#0D5C63] text-white">
        <div className="max-w-5xl mx-auto px-5 py-10 md:py-14">
          <Link to="/amenities" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs font-semibold uppercase tracking-widest" data-testid="ba-back-link"><ArrowLeft size={13} /> Back to Amenities</Link>
          <div className="mt-3 inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1">
            <Sparkles size={14} className="text-[#F26419]" />
            <span className="text-[10px] font-bold tracking-widest uppercase">{amenity.amenity_type}</span>
          </div>
          <h1 className="trust-serif mt-3 text-3xl md:text-5xl leading-tight max-w-3xl" data-testid="ba-amenity-title">Book <span className="text-[#F26419]">{amenity.title}</span></h1>
          <p className="mt-2 text-white/85 text-sm md:text-base max-w-2xl">
            Select a date, confirm availability, and complete your booking online.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 py-10 md:py-12">
        <Stepper step={step} />

        {step === 1 && (
          <StepDate
            amenity={amenity}
            year={year} month={month}
            selectedDate={selectedDate}
            onSelect={setSelectedDate}
            onChangeMonth={changeMonth}
            onNext={proceedToForm}
            isStay={isStay}
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
            onChangeCheckIn={setCheckInDate}
            onChangeCheckOut={setCheckOutDate}
            nights={nights}
          />
        )}
        {step === 2 && (
          <StepForm
            amenity={amenity}
            selectedDate={selectedDate}
            form={form} set={set} toggleIn={toggleIn}
            pricing={pricing}
            voucher={voucher}
            voucherError={voucherError}
            voucherChecking={voucherChecking}
            onApplyVoucher={applyVoucher}
            onRemoveVoucher={removeVoucher}
            onBack={() => setStep(1)}
            onNext={proceedToSummary}
            isStay={isStay}
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
            nights={nights}
          />
        )}
        {step === 3 && (
          <StepSummary
            amenity={amenity}
            selectedDate={selectedDate}
            form={form}
            pricing={pricing}
            voucher={voucher}
            accept={accept} setAccept={setAccept}
            creating={creating}
            onBack={() => setStep(2)}
            onSubmit={submitBooking}
            isStay={isStay}
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
            nights={nights}
          />
        )}
      </section>
    </div>
  );
}

/* --------------------------------- Stepper -------------------------------- */
function Stepper({ step }) {
  const items = [
    { n: 1, label: "Select date" },
    { n: 2, label: "Details" },
    { n: 3, label: "Submit" },
  ];
  return (
    <ol className="flex items-center gap-2 md:gap-4 mb-8 flex-wrap" data-testid="ba-stepper">
      {items.map((it, i) => {
        const active = step === it.n;
        const done = step > it.n;
        return (
          <li key={it.n} className="flex items-center gap-2">
            <div className={`h-7 w-7 md:h-8 md:w-8 rounded-full inline-flex items-center justify-center text-[11px] md:text-xs font-bold ${
              done ? "bg-[#0D5C63] text-white" : active ? "bg-[#F26419] text-white" : "bg-[#EAE2CE] text-[#8a8478]"
            }`}>{done ? <CheckCircle2 size={14} /> : it.n}</div>
            <span className={`text-[11px] md:text-xs tracking-widest uppercase font-bold ${active ? "text-[#0D5C63]" : done ? "text-[#0D5C63]/70" : "text-[#8a8478]"}`}>{it.label}</span>
            {i < items.length - 1 && <span className="h-px w-4 md:w-8 bg-[#DDD3BA]" />}
          </li>
        );
      })}
    </ol>
  );
}

/* -------------------------------- Step 1 --------------------------------- */
function StepDate({ amenity, year, month, selectedDate, onSelect, onChangeMonth, onNext, isStay, checkInDate, checkOutDate, onChangeCheckIn, onChangeCheckOut, nights }) {
  const today = todayYMD();
  return (
    <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 md:gap-10">
      <AvailabilityCalendar
        amenityId={amenity.id}
        year={year} month={month}
        selectedDate={isStay ? "" : selectedDate}
        onSelect={isStay ? () => {} : onSelect}
        onChangeMonth={onChangeMonth}
      />
      <div className="bg-white border border-[#E9E2D2] rounded-2xl p-6 h-fit sticky top-6" data-testid="ba-date-summary">
        <div className="text-[10px] tracking-widest uppercase text-[#F26419] font-bold">Step 1 of 3</div>
        <h2 className="trust-serif text-2xl text-[#1A1A1A] mt-1">{isStay ? "Choose your stay dates" : "Pick your event date"}</h2>
        <p className="text-sm text-[#5B5B5B] mt-2 leading-relaxed">
          {isStay
            ? "Pick your check-in and check-out dates. Please note the standard checkout time is 11:00 AM."
            : "The chart shows which dates are currently free. Green = available, yellow = provisionally booked, red = confirmed booking."}
        </p>

        {isStay ? (
          <div className="mt-5 space-y-3">
            <label className="block" data-testid="ba-checkin-wrap">
              <div className={labelCls}>Check-in Date *</div>
              <input
                type="date"
                className={inputCls}
                value={checkInDate}
                min={today}
                onChange={(e) => onChangeCheckIn(e.target.value)}
                data-testid="ba-checkin"
              />
            </label>
            <label className="block" data-testid="ba-checkout-wrap">
              <div className={labelCls}>Check-out Date *</div>
              <input
                type="date"
                className={inputCls}
                value={checkOutDate}
                min={checkInDate || today}
                onChange={(e) => onChangeCheckOut(e.target.value)}
                data-testid="ba-checkout"
              />
            </label>
            <div className="rounded-lg bg-[#FEF7E4] border border-[#c78e42]/40 px-3 py-2.5 text-xs text-[#8a5a1a] font-semibold inline-flex items-start gap-2" data-testid="ba-checkout-note">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>Please note that checkout time is <b>11:00 AM</b>. Maximum stay is {STAY_MAX_NIGHTS} nights.</span>
            </div>
            {nights > 0 && (
              <div className="mt-2 rounded-lg bg-[#FAFAF7] border border-[#E9E2D2] p-3 text-xs text-[#5B5B5B]" data-testid="ba-stay-summary">
                <div>Total nights: <b className="text-[#0D5C63]">{nights}</b></div>
                {amenity.full_day_charge > 0 && (
                  <div className="mt-1">Rate: <b>{fmtINR(amenity.full_day_charge)}</b> / night{Number(amenity.tax_percent || 0) > 0 && <span className="italic"> + {Number(amenity.tax_percent)}% GST</span>} · Est. base <b>{fmtINR(Number(amenity.full_day_charge) * nights)}</b></div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-5 rounded-lg bg-[#FAFAF7] border border-[#E9E2D2] p-4">
            <div className="text-[10px] tracking-widest uppercase text-[#8a8478]">Selected date</div>
            <div className="trust-serif text-xl text-[#0D5C63] mt-1" data-testid="ba-selected-date-display">
              {selectedDate ? new Date(selectedDate).toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : "— none —"}
            </div>
            {(amenity.half_day_charge || amenity.full_day_charge) ? (
              <div className="mt-3 text-xs text-[#5B5B5B]" data-testid="ba-step1-price">
                {amenity.half_day_charge > 0 && <div>Half day: <b>{fmtINR(amenity.half_day_charge)}</b>{Number(amenity.tax_percent || 0) > 0 && <span className="italic text-[#8a8478]"> + {Number(amenity.tax_percent)}% GST</span>}</div>}
                {amenity.full_day_charge > 0 && <div>Full day: <b>{fmtINR(amenity.full_day_charge)}</b>{Number(amenity.tax_percent || 0) > 0 && <span className="italic text-[#8a8478]"> + {Number(amenity.tax_percent)}% GST</span>}</div>}
                {amenity.security_deposit > 0 && <div>Refundable security deposit: <b>{fmtINR(amenity.security_deposit)}</b></div>}
              </div>
            ) : null}
          </div>
        )}
        <button type="button" onClick={onNext} disabled={isStay ? !(checkInDate && checkOutDate && nights > 0 && nights <= STAY_MAX_NIGHTS) : !selectedDate} data-testid="ba-next-to-form" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] text-white text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-full disabled:opacity-40 disabled:cursor-not-allowed">
          Continue <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

/* -------------------------------- Step 2 --------------------------------- */
function StepForm({ amenity, selectedDate, form, set, toggleIn, pricing, voucher, voucherError, voucherChecking, onApplyVoucher, onRemoveVoucher, onBack, onNext, isStay, checkInDate, checkOutDate, nights }) {
  const seats = amenity.seating_options || [];
  const avs = amenity.av_options || [];
  const durationHours = form.duration === "Half Day" ? 4 : 8;

  return (
    <div className="grid md:grid-cols-[1.6fr_1fr] gap-6 md:gap-10" data-testid="ba-step-form">
      <div className="space-y-8">
        {isStay ? (
          <SectionCard title="Stay Duration">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className={labelCls}>Check-in Date</div>
                <input type="date" readOnly value={checkInDate} className={`${inputCls} bg-[#F5F0E4] cursor-not-allowed`} data-testid="ba-form-checkin" />
              </div>
              <div>
                <div className={labelCls}>Check-out Date</div>
                <input type="date" readOnly value={checkOutDate} className={`${inputCls} bg-[#F5F0E4] cursor-not-allowed`} data-testid="ba-form-checkout" />
              </div>
              <div className="md:col-span-2 inline-flex items-start gap-2 rounded-md bg-[#FEF7E4] border border-[#c78e42]/40 px-3 py-2.5 text-xs text-[#8a5a1a] font-semibold" data-testid="ba-form-checkout-note">
                <Info size={14} className="mt-0.5 shrink-0" />
                <span>Number of nights: <b className="text-[#0D5C63]">{nights}</b>. Checkout time is <b>11:00 AM</b>.</span>
              </div>
            </div>
          </SectionCard>
        ) : (
          <SectionCard title="Booking Duration &amp; Timing">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className={labelCls}>Duration *</div>
                <div className="flex gap-2" data-testid="ba-duration">
                  {["Half Day", "Full Day"].map((d) => {
                    const active = form.duration === d;
                    const disabled = (d === "Half Day" && !amenity.half_day_charge) || (d === "Full Day" && !amenity.full_day_charge);
                    return (
                      <button key={d} type="button" onClick={() => !disabled && set("duration", d)} disabled={disabled} data-testid={`ba-duration-${d.replace(" ", "-").toLowerCase()}`}
                        className={`flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition ${
                          active ? "bg-[#0D5C63] text-white border-[#0D5C63]" : disabled ? "bg-[#F5F0E4] text-[#c7c1b5] cursor-not-allowed border-[#E9E2D2]" : "bg-white text-[#1c1a17] border-[#E9E2D2] hover:border-[#0D5C63]"
                        }`}>{d}</button>
                    );
                  })}
                </div>
                <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#0D5C63] font-semibold bg-[#E7F3F1] border border-[#0D5C63]/25 rounded-md px-2.5 py-1" data-testid="ba-duration-hint">
                  <Info size={12} /> {form.duration === "Half Day" ? "Half day bookings are allowed for 4 hours" : "Full day bookings are allowed for 8 hours"}
                </div>
                {form.duration === "Half Day" && form.start_time && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#8a5a1a] font-semibold bg-[#FFF3DF] border border-[#c78e42]/40 rounded-md px-2.5 py-1" data-testid="ba-slot-hint">
                    <Info size={12} /> {Number((form.start_time || "00:00").split(":")[0]) < 12 ? "You are booking the morning (AM) slot. Afternoon stays open for others." : "You are booking the afternoon (PM) slot. Morning stays open for others."}
                  </div>
                )}
              </div>
              <div>
                <div className={labelCls}>Event Start Time *</div>
                <input type="time" className={inputCls} value={form.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid="ba-start-time" />
              </div>
              <div>
                <div className={labelCls}>Event End Time (auto-calculated · +{durationHours}h)</div>
                <input type="time" className={`${inputCls} bg-[#F5F0E4] cursor-not-allowed`} value={form.end_time} readOnly data-testid="ba-end-time" />
              </div>
            </div>
          </SectionCard>
        )}

        <SectionCard title="Applicant Details">
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Name *" value={form.applicant_name} onChange={(v) => set("applicant_name", v)} testid="ba-applicant-name" />
            <TextField label="Mobile Number *" value={form.mobile} onChange={(v) => set("mobile", v)} testid="ba-mobile" placeholder="e.g. +91 98765 43210" />
            <TextField label="Email Address *" type="email" value={form.email} onChange={(v) => set("email", v)} testid="ba-email" />
            <TextField label="Institute / Department / Organisation / Company" value={form.organization} onChange={(v) => set("organization", v)} testid="ba-organization" />
          </div>
        </SectionCard>

        <SectionCard title={isStay ? "Guest & Purpose Details" : "Event Details"}>
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label={isStay ? "Purpose of Stay *" : "Name of the Event *"} value={form.event_name} onChange={(v) => set("event_name", v)} testid="ba-event-name" />
            <TextField label={isStay ? "Guest Category" : "Type of Event"} value={form.event_type} onChange={(v) => set("event_type", v)} testid="ba-event-type" placeholder={isStay ? "e.g. Visiting Faculty, Family, Alumni" : "e.g. Conference, Workshop, Cultural"} />
            <TextField label={isStay ? "Number of Guests" : "Expected Number of Attendees"} type="number" value={form.attendees} onChange={(v) => set("attendees", v)} testid="ba-attendees" />
            <div className="md:col-span-2">
              <div className={labelCls}>{isStay ? "Additional Notes / Special Requests" : "Brief Details / Purpose of the Event"}</div>
              <textarea rows={3} className={inputCls} value={form.event_purpose} onChange={(e) => set("event_purpose", e.target.value)} data-testid="ba-purpose" />
            </div>
          </div>
        </SectionCard>

        {(seats.length > 0 || avs.length > 0) && (
          <SectionCard title="Facilities &amp; Requirements">
            {seats.length > 0 && (
              <div className="mb-5">
                <div className={labelCls}>Seating Arrangement <span className="text-[#6b655b] normal-case tracking-normal">(included in cost)</span></div>
                <select className={inputCls} value={form.seating} onChange={(e) => set("seating", e.target.value)} data-testid="ba-seating">
                  <option value="">Not required</option>
                  {seats.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            {avs.length > 0 && (
              <div>
                <div className={labelCls}>Audio-Visual Equipment <span className="text-[#6b655b] normal-case tracking-normal">(included in cost)</span></div>
                <CheckList items={avs.map((n) => ({ name: n }))} selected={form.av_equipment} onToggle={(n, on) => toggleIn("av_equipment", n, on)} testidPrefix="ba-av" />
              </div>
            )}
          </SectionCard>
        )}

        <SectionCard title="Billing &amp; Remarks">
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Billing Name" value={form.billing_name} onChange={(v) => set("billing_name", v)} testid="ba-billing-name" placeholder="Leave blank to use applicant name" />
            <TextField label="GST Number (if applicable)" value={form.gst_number} onChange={(v) => set("gst_number", v)} testid="ba-gst" />
            <div className="md:col-span-2">
              <div className={labelCls}>Billing Address</div>
              <textarea rows={2} className={inputCls} value={form.billing_address} onChange={(e) => set("billing_address", e.target.value)} data-testid="ba-billing-address" />
            </div>
            <div className="md:col-span-2">
              <div className={labelCls}>Additional Remarks / Special Instructions</div>
              <textarea rows={2} className={inputCls} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} data-testid="ba-remarks" />
            </div>
          </div>
        </SectionCard>

        <SectionCard title='Waiver Voucher <span style="color:#D90368">*</span>'>
          <p className="text-xs text-[#5B5B5B] mb-3" data-testid="ba-voucher-help">A voucher code issued by GS Mandal is required to book. It may waive the full charge or give a discount; any balance is paid online. Don't have one? Contact <a href="mailto:bhagyashree.pande@gsmandal.org" className="text-[#0D5C63] font-semibold hover:underline">bhagyashree.pande@gsmandal.org</a>.</p>
          {voucher ? (
            <div className="flex items-center justify-between gap-3 bg-[#E7F3F1] border border-[#0D5C63]/30 rounded-md px-4 py-3" data-testid="ba-voucher-applied">
              <div>
                <div className="text-[10px] tracking-widest uppercase text-[#0D5C63] font-bold">Voucher applied</div>
                <div className="font-mono text-sm text-[#0D5C63]">{voucher.code}</div>
                <div className="text-[11px] text-[#5B5B5B]" data-testid="ba-voucher-effect">{voucher.discount_label || "100% waiver"} · {pricing.isWaived ? "Total waived, you'll skip the payment step" : `Pay the balance of ${fmtINR(pricing.total)} online`}</div>
              </div>
              <button type="button" onClick={onRemoveVoucher} data-testid="ba-voucher-remove" className="text-xs font-semibold text-red-700 hover:underline">Remove</button>
            </div>
          ) : (
            <div className="flex gap-2 items-start">
              <input
                data-testid="ba-voucher-input"
                className={`${inputCls} font-mono uppercase flex-1`}
                value={form.voucher_code}
                onChange={(e) => set("voucher_code", e.target.value.toUpperCase())}
                placeholder="GSAM-XXXX-XXXX-XX"
                maxLength={32}
              />
              <button type="button" onClick={onApplyVoucher} disabled={voucherChecking || !form.voucher_code.trim()} data-testid="ba-voucher-apply"
                      className="inline-flex items-center gap-1.5 bg-[#0D5C63] hover:bg-[#083E43] disabled:opacity-40 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-md">
                {voucherChecking ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Apply
              </button>
            </div>
          )}
          {voucherError && !voucher && <div className="mt-2 text-xs text-red-700" data-testid="ba-voucher-error">{voucherError}</div>}
        </SectionCard>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button type="button" onClick={onBack} data-testid="ba-form-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5B5B5B] hover:text-[#0D5C63] uppercase tracking-widest"><ArrowLeft size={14} /> Back</button>
          <button type="button" onClick={onNext} data-testid="ba-form-next" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 rounded-full">Review booking <ArrowRight size={14} /></button>
        </div>
      </div>

      <PricingSidebar amenity={amenity} pricing={pricing} selectedDate={selectedDate} duration={form.duration} isStay={isStay} checkInDate={checkInDate} checkOutDate={checkOutDate} nights={nights} />
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="bg-white border border-[#E9E2D2] rounded-2xl p-6">
      <h3 className="trust-serif text-xl text-[#1A1A1A] mb-4" dangerouslySetInnerHTML={{ __html: title }} />
      {children}
    </div>
  );
}

function TextField({ label, value, onChange, testid, type = "text", placeholder = "" }) {
  return (
    <label className="block">
      <div className={labelCls}>{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputCls} data-testid={testid} />
    </label>
  );
}

function CheckList({ items, selected, onToggle, showPrice, testidPrefix }) {
  const set = new Set(selected || []);
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid={`${testidPrefix}-list`}>
      {(items || []).map((it) => {
        const on = set.has(it.name);
        return (
          <label key={it.name} className={`flex items-center gap-2.5 border rounded-md px-3 py-2 cursor-pointer text-sm ${on ? "bg-[#E7F3F1] border-[#0D5C63]" : "bg-white border-[#E9E2D2] hover:border-[#0D5C63]"}`} data-testid={`${testidPrefix}-item-${it.name}`}>
            <input type="checkbox" className="h-4 w-4 accent-[#0D5C63]" checked={on} onChange={(e) => onToggle(it.name, e.target.checked)} />
            <span className="flex-1">{it.name}</span>
            {showPrice && <span className="text-[#0D5C63] text-xs font-semibold">{fmtINR(it.price || 0)}</span>}
          </label>
        );
      })}
    </div>
  );
}

function PricingSidebar({ amenity, pricing, selectedDate, duration, isStay, checkInDate, checkOutDate, nights }) {
  if (!pricing) return null;
  return (
    <aside className="bg-white border border-[#E9E2D2] rounded-2xl p-5 md:p-6 h-fit sticky top-6" data-testid="ba-pricing-sidebar">
      <div className="text-[10px] tracking-widest uppercase text-[#F26419] font-bold">Booking Summary</div>
      <div className="trust-serif text-xl text-[#1A1A1A] mt-1">{amenity.title}</div>
      <div className="mt-1 text-xs text-[#5B5B5B]">
        {isStay
          ? <>{checkInDate ? new Date(checkInDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"} → {checkOutDate ? new Date(checkOutDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"} · {nights} night{nights !== 1 ? "s" : ""}</>
          : <>{selectedDate ? new Date(selectedDate).toLocaleDateString("en-IN", { weekday: "short", year: "numeric", month: "short", day: "numeric" }) : "—"} · {duration}</>
        }
      </div>
      <div className="mt-5 space-y-1.5 text-sm text-[#3E3E3E]">
        <Row label={isStay ? `Stay (${nights} × ${fmtINR(amenity.full_day_charge || 0)}/night)` : `Base (${duration})`} value={fmtINR(pricing.base)} />
        {pricing.taxPct > 0 && <Row label={`Tax (${pricing.taxPct}%)`} value={fmtINR(pricing.tax)} />}
        {pricing.deposit > 0 && <Row label="Security deposit (refundable)" value={fmtINR(pricing.deposit)} />}
        {pricing.hasVoucher && (
          <>
            <Row label="Sub-total" value={fmtINR(pricing.gross)} />
            <Row label="Voucher discount" value={`- ${fmtINR(pricing.waived)}`} sub />
          </>
        )}
      </div>
      <div className="mt-4 border-t border-[#E9E2D2] pt-3 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-[#8a8478] font-bold">{pricing.isWaived ? "Total Waived" : "Total Payable"}</div>
        <div className="trust-serif text-2xl text-[#0D5C63]" data-testid="ba-total">{fmtINR(pricing.total)}</div>
      </div>
      {isStay && (
        <div className="mt-3 rounded-md bg-[#FEF7E4] border border-[#c78e42]/40 px-2.5 py-2 text-[11px] text-[#8a5a1a] font-semibold inline-flex items-start gap-1.5" data-testid="ba-sidebar-checkout-note">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span>Checkout time is 11:00 AM.</span>
        </div>
      )}
    </aside>
  );
}

function Row({ label, value, sub }) {
  return (
    <div className={`flex items-center justify-between gap-2 ${sub ? "text-[#5B5B5B] text-xs pl-3" : ""}`}>
      <span className="truncate">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

/* -------------------------------- Step 3 --------------------------------- */
function StepSummary({ amenity, selectedDate, form, pricing, voucher, accept, setAccept, creating, onBack, onSubmit, isStay, checkInDate, checkOutDate, nights }) {
  return (
    <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 md:gap-10" data-testid="ba-step-summary">
      <div className="space-y-6">
        <SectionCard title="Booking Summary">
          <dl className="grid md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Info2 label="Amenity" value={amenity.title} />
            {isStay ? (
              <>
                <Info2 label="Stay" value={`${nights} night${nights !== 1 ? "s" : ""}`} />
                <Info2 label="Check-in Date" value={new Date(checkInDate).toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} />
                <Info2 label="Check-out Date" value={`${new Date(checkOutDate).toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} · 11:00 AM`} />
              </>
            ) : (
              <>
                <Info2 label="Event Date" value={new Date(selectedDate).toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} />
                <Info2 label="Duration" value={form.duration} />
                <Info2 label="Time" value={`${form.start_time || "—"} to ${form.end_time || "—"}`} />
              </>
            )}
            <Info2 label="Applicant" value={form.applicant_name} />
            <Info2 label="Mobile" value={form.mobile} />
            <Info2 label="Email" value={form.email} />
            <Info2 label="Organisation" value={form.organization || "—"} />
            <Info2 label={isStay ? "Purpose of Stay" : "Event Name"} value={form.event_name} />
            <Info2 label={isStay ? "Guest Category" : "Event Type"} value={form.event_type || "—"} />
            <Info2 label={isStay ? "Guests" : "Attendees"} value={form.attendees || "—"} />
            <Info2 label="Seating" value={form.seating || "None"} />
            <Info2 label="A-V Equipment" value={(form.av_equipment || []).join(", ") || "None"} full />
            <Info2 label="Billing Name" value={form.billing_name || form.applicant_name} />
            <Info2 label="GST No." value={form.gst_number || "—"} />
            <Info2 label="Billing Address" value={form.billing_address || "—"} full />
            <Info2 label="Remarks" value={form.remarks || "—"} full />
          </dl>
          {isStay && (
            <div className="mt-4 rounded-md bg-[#FEF7E4] border border-[#c78e42]/40 px-3 py-2 text-xs text-[#8a5a1a] font-semibold inline-flex items-start gap-2" data-testid="ba-summary-checkout-note">
              <Info size={14} className="mt-0.5 shrink-0" />
              <span>Please note: checkout time is <b>11:00 AM</b>.</span>
            </div>
          )}
        </SectionCard>

        <SectionCard title="Terms, Cancellation Policy, Usage Guidelines &amp; Privacy Policy">
          <PolicyLinks amenity={amenity} />
        </SectionCard>

        <div className="bg-[#FEF7E4] border border-[#c78e42]/40 rounded-2xl p-4 flex items-start gap-3" data-testid="ba-accept-block">
          <input id="ba-accept" type="checkbox" className="mt-1 h-4 w-4 accent-[#0D5C63]" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="ba-accept-checkbox" />
          <label htmlFor="ba-accept" className="text-sm text-[#3E3E3E] leading-relaxed">
            <span className="font-semibold">*</span> I have read and accept the <b>Booking Terms</b>, <b>Cancellation / Refund Policy</b>, <b>Usage Guidelines</b> and <b>Privacy Policy</b>. I understand that {voucher ? (pricing.isWaived ? <>my <b>waiver voucher</b> will fully waive the charges once the booking is confirmed.</> : <>after the voucher discount I will pay the balance of <b>{fmtINR(pricing.total)}</b> online, and the booking is confirmed once the payment succeeds.</>) : <>my application will be reviewed by GS Mandal and I will be contacted once it is confirmed.</>}
          </label>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button type="button" onClick={onBack} data-testid="ba-sum-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5B5B5B] hover:text-[#0D5C63] uppercase tracking-widest"><ArrowLeft size={14} /> Back</button>
          <button type="button" onClick={onSubmit} disabled={!accept || creating} data-testid="ba-sum-submit" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 rounded-full disabled:opacity-40 disabled:cursor-not-allowed">
            {creating ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : (voucher ? (pricing.isWaived ? <>Confirm Booking (Waived) <ArrowRight size={14} /></> : <>Submit &amp; Pay {fmtINR(pricing.total)} <ArrowRight size={14} /></>) : <>Submit Application <ArrowRight size={14} /></>)}
          </button>
        </div>
      </div>

      <PricingSidebar amenity={amenity} pricing={pricing} selectedDate={selectedDate} duration={form.duration} isStay={isStay} checkInDate={checkInDate} checkOutDate={checkOutDate} nights={nights} />
    </div>
  );
}

function Info2({ label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10px] tracking-widest uppercase text-[#8a8478] font-bold">{label}</div>
      <div className="text-[#1c1a17] mt-0.5 break-words" data-testid={`info2-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{value || "—"}</div>
    </div>
  );
}

/* --------------- Policy links (Terms · Cancellation · Usage · Privacy) -------------- */
function PolicyLinks({ amenity }) {
  const [openKey, setOpenKey] = useState(null);
  const items = [
    { key: "terms", label: "Booking Terms", body: amenity.terms_and_conditions },
    { key: "cancel", label: "Cancellation / Refund Policy", body: amenity.cancellation_policy },
    { key: "usage", label: "Usage Guidelines", body: amenity.usage_guidelines },
    { key: "privacy", label: "Privacy Policy", body: amenity.privacy_policy },
  ];
  const current = items.find((i) => i.key === openKey);
  return (
    <>
      <div className="text-sm text-[#3E3E3E] leading-relaxed" data-testid="ba-terms-block">
        Please review the following before submitting your booking:
        <ul className="mt-3 space-y-2">
          {items.map((it) => (
            <li key={it.key}>
              <button
                type="button"
                onClick={() => setOpenKey(it.key)}
                data-testid={`ba-policy-link-${it.key}`}
                className="inline-flex items-center gap-2 text-[#0D5C63] font-semibold underline underline-offset-4 decoration-[#c78e42] hover:text-[#F26419] hover:decoration-[#F26419] transition"
              >
                {it.label}
                {!it.body && <span className="text-[10px] text-[#8a8478] italic uppercase tracking-widest no-underline">· not provided</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {current && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          onClick={() => setOpenKey(null)}
          data-testid="ba-policy-modal"
        >
          <div
            className="bg-[#FFFCF3] border border-[#E9E2D2] rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 md:p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] tracking-widest uppercase text-[#F26419] font-bold">Policy</div>
                <h3 className="trust-serif text-2xl text-[#1A1A1A] mt-1">{current.label}</h3>
                <div className="mt-1 text-[11px] text-[#8a8478]">{amenity.title}</div>
              </div>
              <button
                type="button"
                onClick={() => setOpenKey(null)}
                aria-label="Close"
                className="text-[#5B5B5B] hover:text-[#0D5C63] rounded-full border border-[#E9E2D2] w-8 h-8 flex items-center justify-center"
                data-testid="ba-policy-modal-close"
              >
                ×
              </button>
            </div>
            <div className="mt-4 border-t border-[#E9E2D2] pt-4 prose prose-sm max-w-none text-[#3E3E3E]" data-testid="ba-policy-modal-body">
              {current.body
                ? <div dangerouslySetInnerHTML={{ __html: current.body }} />
                : <p className="text-[#8a8478] italic">The trust administration has not published a {current.label.toLowerCase()} for this amenity yet.</p>}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setOpenKey(null)}
                className="bg-[#0D5C63] hover:bg-[#F26419] text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full transition"
                data-testid="ba-policy-modal-ok"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ================== Confirmation page (post-payment redirect) ============= */
export function BookAmenityConfirmation() {
  const { bookingRef } = useParams();
  const [sp] = useSearchParams();
  const paymentStatus = (sp.get("payment") || "").toLowerCase();
  const [booking, setBooking] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancel = false;
    let attempts = 0;
    const load = async () => {
      try {
        const { data } = await api.get(`/public/amenity-bookings/${bookingRef}`);
        if (cancel) return;
        setBooking(data);
        // If the callback just landed us here but the payment webhook hasn't
        // updated the DB yet, retry briefly to catch the transition.
        if (paymentStatus === "success" && data.status !== "CONFIRMED" && attempts < 4) {
          attempts += 1;
          setTimeout(load, 1500);
        }
      } catch (e) {
        if (!cancel) setErr(e?.response?.data?.detail || "Booking not found");
      }
    };
    load();
    return () => { cancel = true; };
  }, [bookingRef, paymentStatus]);

  const downloadReceipt = async () => {
    try {
      const res = await api.get(`/public/amenity-bookings/${bookingRef}/receipt`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Receipt-${bookingRef}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download receipt");
    }
  };

  if (err) return <div className="max-w-3xl mx-auto px-5 py-20"><div className="bg-red-50 border border-red-200 text-red-800 rounded-2xl p-6" data-testid="ba-conf-error"><AlertTriangle size={18} className="inline mr-1" />{err}</div></div>;
  if (!booking) return <div className="max-w-3xl mx-auto px-5 py-20 text-sm text-[#5B5B5B]" data-testid="ba-conf-loading"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>;

  const isConfirmed = booking.status === "CONFIRMED";
  const isPending = booking.status === "PENDING_VERIFICATION";
  const isProvisional = booking.status === "PROVISIONAL";
  const isRejected = booking.status === "REJECTED";
  const isFailed = paymentStatus && !["success", "review"].includes(paymentStatus);

  return (
    <div className="bg-[#FAF6EB] min-h-screen" data-testid="ba-confirmation">
      <div className="max-w-3xl mx-auto px-5 py-14 md:py-20">
        <div className="bg-white border border-[#E9E2D2] rounded-2xl p-6 md:p-10 text-center">
          {isConfirmed ? (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-[#E7F3F1] text-[#0D5C63] inline-flex items-center justify-center">
                <CheckCircle2 size={36} />
              </div>
              <h1 className="trust-serif text-3xl md:text-4xl text-[#1A1A1A] mt-4" data-testid="ba-conf-title">Booking Confirmed</h1>
              <p className="text-sm text-[#5B5B5B] mt-2">Your amenity booking has been confirmed. A receipt has been emailed to <b>{booking.email}</b>.</p>
            </>
          ) : booking.voucher_partial && ["PROVISIONAL", "FAILED_PAYMENT"].includes(booking.status) ? (
            <BalanceDue booking={booking} failed={isFailed} paymentStatus={paymentStatus} />
          ) : isProvisional ? (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-[#FEF7E4] text-[#8a5a1a] inline-flex items-center justify-center">
                <Clock size={32} />
              </div>
              <h1 className="trust-serif text-3xl md:text-4xl text-[#1A1A1A] mt-4" data-testid="ba-conf-title">Application Under Review</h1>
              <p className="text-sm text-[#5B5B5B] mt-2 max-w-lg mx-auto leading-relaxed">
                Thank you for your booking request. Your application is currently under review by the GS Mandal team.
                We will get back to you soon at <b>{booking.email}</b> with next steps and payment instructions.
              </p>
            </>
          ) : isPending ? (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-[#FEF7E4] text-[#8a5a1a] inline-flex items-center justify-center">
                <Clock size={32} />
              </div>
              <h1 className="trust-serif text-3xl md:text-4xl text-[#1A1A1A] mt-4" data-testid="ba-conf-title">Awaiting Verification</h1>
              <p className="text-sm text-[#5B5B5B] mt-2">Your bank-transfer submission is being reviewed. You&rsquo;ll receive a confirmation email once verified.</p>
            </>
          ) : isRejected ? (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-[#FDECEC] text-[#B02020] inline-flex items-center justify-center">
                <XCircle size={32} />
              </div>
              <h1 className="trust-serif text-3xl md:text-4xl text-[#1A1A1A] mt-4" data-testid="ba-conf-title">Application Rejected</h1>
              <p className="text-sm text-[#5B5B5B] mt-2 max-w-lg mx-auto">{booking.rejection_reason || "Unfortunately, this booking application was not approved."}</p>
            </>
          ) : isFailed ? (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-[#FDECEC] text-[#B02020] inline-flex items-center justify-center">
                <XCircle size={32} />
              </div>
              <h1 className="trust-serif text-3xl md:text-4xl text-[#1A1A1A] mt-4" data-testid="ba-conf-title">Payment {paymentStatus === "cancelled" ? "Cancelled" : "Failed"}</h1>
              <p className="text-sm text-[#5B5B5B] mt-2">No amount was charged. You can retry the payment from your booking page.</p>
            </>
          ) : (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-[#FEF7E4] text-[#8a5a1a] inline-flex items-center justify-center">
                <Info size={30} />
              </div>
              <h1 className="trust-serif text-3xl md:text-4xl text-[#1A1A1A] mt-4" data-testid="ba-conf-title">Booking Received</h1>
              <p className="text-sm text-[#5B5B5B] mt-2">Status: <b>{booking.status}</b></p>
            </>
          )}

          <div className="mt-6 grid sm:grid-cols-2 gap-3 text-left bg-[#FAFAF7] border border-[#E9E2D2] rounded-xl p-4">
            <Info2 label="Booking Ref." value={booking.booking_ref} />
            <Info2 label="Amenity" value={booking.amenity_title} />
            {booking.is_stay || booking.check_in_date ? (
              <>
                <Info2 label="Check-in" value={`${new Date(booking.check_in_date || booking.event_date).toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}`} />
                <Info2 label="Check-out" value={`${new Date(booking.check_out_date).toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} · 11:00 AM`} />
              </>
            ) : (
              <>
                <Info2 label="Event Date" value={new Date(booking.event_date).toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })} />
                <Info2 label="Duration" value={booking.duration} />
              </>
            )}
            <Info2 label="Amount" value={fmtINR(booking.total_amount)} />
            <Info2 label="Payment Method" value={booking.payment_method || (isProvisional ? "To be advised by GS Mandal" : (isFailed ? "—" : "Pending"))} />
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {isConfirmed && (
              <button type="button" onClick={downloadReceipt} data-testid="ba-download-receipt" className="inline-flex items-center gap-2 bg-[#0D5C63] hover:bg-[#083E43] text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full">
                <Download size={14} /> Download Receipt
              </button>
            )}
            <Link to="/amenities" data-testid="ba-back-to-amenities" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0D5C63] hover:underline">Back to Amenities <ArrowRight size={14} /></Link>
          </div>

          <div className="mt-6 border-t border-[#E9E2D2] pt-4 text-[11px] text-[#8a8478] leading-relaxed">
            For assistance, contact GS Mandal at <b>info@gsmandal.org</b>.
          </div>
        </div>
      </div>
    </div>
  );
}
